import { parse } from 'query-string';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import CourseGroupList from 'src/components/CourseGroupList';
import Select from 'src/components/FormElements/Select';
import { getCoursesGroupedByName, getCoursesGroupedByCategory } from 'src/helpers/api';
import { ITitleItem } from 'src/types/Courses';
import { groupBy } from 'src/utils/arrayUtils';

const normalizeTitle = (title: string) => title.toLowerCase()
  .replace(/^\((.*?)\)/, '')
  .replace(/[^a-z0-9]/g, '');
const getFirstLetter = (item: { title: string }) => normalizeTitle(item.title).charAt(0).toUpperCase();

type GroupList = Array<{
  id: string | number;
  title: string,
  groups: ITitleItem[],
}>;

const CourseIndex = (props: RouteComponentProps) => {
  const params = React.useMemo(() => {
    const object = parse(props.location.search);

    return {
      group: 'group' in object && object.group === 'title' ? 'title' : 'category',
      order: 'order' in object && object.order === 'descending' ? 'descending' : 'ascending',
    } as {
      group: 'title' | 'category';
      order: 'ascending' | 'descending';
    }
  }, [props.location.search]);

  const [groups, setGroups] = React.useState<GroupList>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [group, setGroup] = React.useState<'title' | 'category'>(params.group);
  const [order, setOrder] = React.useState<'ascending' | 'descending'>(params.order);

  const orderedGroups = React.useMemo(() => {
    const sortedGroups = groups.sort((a, b) => {
      if (a.title < b.title) {
        return order === 'ascending' ? -1 : 1;
      }
      if (a.title > b.title) {
        return order === 'ascending' ? 1 : -1;
      }

      return 0;
    });

    return sortedGroups;
  }, [groups, order]);

  React.useEffect(() => {
    props.history.replace({
      search: `?group=${group}&order=${order}`,
    })
  }, [group, order]);

  const handleGroupAndOrderChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    const parts = value.split(':');

    setGroup(parts[0] === 'title' ? 'title' : 'category');
    setOrder(parts[1] === 'asc' ? 'ascending' : 'descending');
  };

  React.useEffect(() => {
    setIsLoading(true);

    if (group === 'category') {
      getCoursesGroupedByCategory().then((categories) => {
        const nextGroups: GroupList = [];

        for (const category of categories) {
          nextGroups.push({
            id: category.categoryId,
            title: category.name,
            groups: category.coursesGroupedByName,
          });
        }

        setGroups(nextGroups);
        setIsLoading(false);
      });

      return;
    }

    getCoursesGroupedByName().then((titles) => {
      const nextGroups = groupBy(titles, getFirstLetter);
      const lists = Object.keys(nextGroups).map((key) => ({
        id: key,
        title: key,
        groups: nextGroups[key],
      })) as GroupList;

      setGroups(lists);
      setIsLoading(false);
    });
  }, [group]);

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-lg font-extrabold m-0 uppercase">
          {`Find kursus efter ${group === 'title' ? 'titel' : 'emner'}`}
        </h3>

        <Select
          onChange={handleGroupAndOrderChange}
          variant="light"
        >
          <option value="title:asc">Vis kurser efter titel - sortér A-Z</option>
          <option value="title:desc">Vis kurser efter titel - sortér Z-A</option>
          <option value="category:asc">Vis kurser efter emne - sortér A-Z</option>
          <option value="category:desc">Vis kurser efter emne - sortér Z-A</option>
        </Select>
      </div>

      <CourseGroupList
        isLoading={isLoading}
        lists={orderedGroups}
        onItemClick={(id, groupId) => {
          if (group === 'category') {
            props.history.push(`/titler/${groupId}/${id}`)
          } else {
            props.history.push(`/titler/${id}`)
          }
        }}
      />
    </div>
  );
};

export default withRouter(CourseIndex);



// WEBPACK FOOTER //
// ./src/Pages/CoursePortal/Home/CourseIndex.component.tsx